import { ValidatorFn, Validators } from '@angular/forms';
import { ColumnaTabla } from '@compartido/componentes/tabla-datos/tabla-datos-dto';
import { TextosCrud } from '../i18n/crud-textos';
import { CampoCrud, ColumnaCrud, TextosEntidadCrud } from '../modelos/descriptor-crud-dto';

/** Moneda con la que se muestran los importes (la moneda real por conjunto está en la configuración financiera). */
export const MONEDA_POR_DEFECTO = 'USD';
const PATRON_UUID = '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$';
const LONGITUD_FECHA = 10;

/** `unitCode` → `Unit code`: respaldo cuando un campo no tiene etiqueta en los textos. */
export function humanizar(nombre: string): string {
  const texto = nombre.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

export function etiquetaCampo(textos: TextosEntidadCrud, nombre: string): string {
  return textos.campos[nombre] ?? humanizar(nombre);
}

export function etiquetaEnumerado(textos: TextosEntidadCrud, enumerado: string | undefined, valor: unknown): string {
  if (valor === null || valor === undefined || valor === '') return '';
  const texto = String(valor);
  return (enumerado ? textos.enumerados[enumerado]?.[texto] : undefined) ?? texto;
}

export function formatearFecha(valor: unknown, locale: string): string {
  if (typeof valor !== 'string' || valor === '') return '';
  // Las fechas sin hora (`YYYY-MM-DD`) se muestran tal cual para no moverlas por la zona horaria.
  const fecha = valor.length === LONGITUD_FECHA ? new Date(`${valor}T00:00:00`) : new Date(valor);
  return Number.isNaN(fecha.getTime()) ? valor : new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(fecha);
}

export function formatearFechaHora(valor: unknown, locale: string): string {
  if (typeof valor !== 'string' || valor === '') return '';
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime())
    ? valor
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(fecha);
}

export function formatearDinero(valor: unknown, locale: string): string {
  if (valor === null || valor === undefined || valor === '') return '';
  return new Intl.NumberFormat(locale, { style: 'currency', currency: MONEDA_POR_DEFECTO }).format(Number(valor));
}

export function formatearNumero(valor: unknown, locale: string): string {
  if (valor === null || valor === undefined || valor === '') return '';
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 6 }).format(Number(valor));
}

/** Texto de una celda de la tabla según el tipo de su columna. */
export function textoDeColumna(
  fila: Record<string, unknown>,
  columna: ColumnaCrud,
  textos: TextosEntidadCrud,
  genericos: TextosCrud,
  locale: string,
  resolverReferencia: (clave: NonNullable<ColumnaCrud['referencia']>, id: string) => string,
): string {
  const valor = fila[columna.campo];
  switch (columna.tipo) {
    case 'fecha':
      return formatearFecha(valor, locale);
    case 'fechaHora':
      return formatearFechaHora(valor, locale);
    case 'dinero':
      return formatearDinero(valor, locale);
    case 'numero':
      return formatearNumero(valor, locale);
    case 'booleano':
      return valor === true ? genericos.si : valor === false ? genericos.no : '';
    case 'enumerado':
    case 'estado':
      return etiquetaEnumerado(textos, columna.enumerado, valor);
    case 'referencia':
      return typeof valor === 'string' && columna.referencia ? resolverReferencia(columna.referencia, valor) : '';
    default:
      return valor === null || valor === undefined ? '' : String(valor);
  }
}

export function validadoresDeCampo(campo: CampoCrud): ValidatorFn[] {
  const validadores: ValidatorFn[] = [];
  if (campo.requerido && campo.tipo !== 'booleano') {
    validadores.push(campo.multiple ? Validators.minLength(1) : Validators.required);
  }
  if (campo.maxLongitud) validadores.push(Validators.maxLength(campo.maxLongitud));
  if (campo.minimo !== undefined) validadores.push(Validators.min(campo.minimo));
  if (campo.maximo !== undefined) validadores.push(Validators.max(campo.maximo));
  if (campo.patron) validadores.push(Validators.pattern(campo.patron));
  if (campo.tipo === 'referencia' && !campo.referencia && !campo.opcionesRuta && !campo.multiple) {
    validadores.push(Validators.pattern(PATRON_UUID));
  }
  return validadores;
}

/** Valor del formulario a partir del dato del backend (fechas con hora → `datetime-local`). */
export function valorParaFormulario(campo: CampoCrud, valor: unknown): unknown {
  if (campo.tipo === 'booleano') return valor === true;
  if (campo.multiple) return Array.isArray(valor) ? valor : [];
  if (valor === null || valor === undefined) return campo.tipo === 'entero' || campo.tipo === 'decimal' ? null : '';
  if (campo.tipo === 'fechaHora' && typeof valor === 'string') return aFechaHoraLocal(valor);
  return valor;
}

/** Cuerpo para el backend a partir de los valores del formulario: vacíos → `null`, números y fechas con su formato. */
export function valorParaCuerpo(campo: CampoCrud, valor: unknown): unknown {
  if (campo.tipo === 'booleano') return valor === true;
  if (campo.multiple) return Array.isArray(valor) ? valor : [];
  if (valor === null || valor === undefined || valor === '') return null;
  switch (campo.tipo) {
    case 'entero':
    case 'decimal':
      return Number(valor);
    case 'fechaHora':
      return new Date(String(valor)).toISOString();
    case 'texto':
    case 'textoLargo':
      return String(valor).trim() || null;
    default:
      return valor;
  }
}

/** `2026-11-10T23:00:00Z` → `2026-11-10T18:00` (hora local del navegador, para `datetime-local`). */
function aFechaHoraLocal(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return '';
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${fecha.getFullYear()}-${dos(fecha.getMonth() + 1)}-${dos(fecha.getDate())}T${dos(fecha.getHours())}:${dos(fecha.getMinutes())}`;
}

export function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

export interface FilaCrud {
  /** Registro original del backend, para abrir la ficha y ejecutar acciones. */
  dto: Record<string, unknown>;
  [campo: string]: unknown;
}

/** Columnas de `app-tabla-datos` a partir de las columnas del descriptor (encabezados traducidos). */
export function columnasDeTabla(columnas: ColumnaCrud[], textos: TextosEntidadCrud): ColumnaTabla[] {
  return columnas.map((columna) => {
    const numerica = columna.tipo === 'dinero' || columna.tipo === 'numero';
    return {
      campo: columna.campo,
      encabezado: etiquetaCampo(textos, columna.campo),
      tipo: columna.identificador ? 'identificador' : columna.tipo === 'estado' ? 'estado' : undefined,
      campoSubtitulo: columna.subtitulo ? `_sub_${columna.subtitulo}` : undefined,
      alineacion: numerica ? 'right' : columna.tipo === 'estado' || columna.tipo === 'booleano' ? 'center' : undefined,
      ordenable: columna.ordenable,
    };
  });
}

/** Filas de `app-tabla-datos`: cada celda ya es un texto formateado; `dto` conserva el registro. */
export function filasDeTabla(
  registros: Record<string, unknown>[],
  columnas: ColumnaCrud[],
  textos: TextosEntidadCrud,
  genericos: TextosCrud,
  locale: string,
  resolverReferencia: (clave: NonNullable<ColumnaCrud['referencia']>, id: string) => string,
): FilaCrud[] {
  return registros.map((registro) => {
    const fila: FilaCrud = { dto: registro };
    for (const columna of columnas) {
      fila[columna.campo] = textoDeColumna(registro, columna, textos, genericos, locale, resolverReferencia);
      if (columna.subtitulo) {
        fila[`_sub_${columna.subtitulo}`] = String(registro[columna.subtitulo] ?? '');
      }
    }
    return fila;
  });
}
